import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { DialogActions, Typography } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { usePostConvertTreatmentTemplate } from "api/services/treatment/hooks";
import {
  type ConvertTreatmentTemplatePayload,
  ConvertTreatmentTemplatePayloadSchema,
} from "api/services/treatment/schemas/Treatment";
import { DURATION_WEEKS_OPTIONS } from "routes/patients/views/Treatment/components/TreatmentEditor/SettingsSidebar/SettingsSidebar";
import {
  Divider,
  Section,
  SectionTitle,
} from "routes/patients/views/Treatment/components/TreatmentEditor/SettingsSidebar/styles";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISelect } from "shared/atoms/MUI";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { Notification } from "shared/atoms/Notification";
import { useGroupOptions } from "utils/form";

interface ConvertTemplateModalProps {
  templateID: number;
  onClose: () => void;
}

export const ConvertTemplateModal: React.FC<ConvertTemplateModalProps> = ({ templateID, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const groupOptions = useGroupOptions();
  const postConvertTreatmentTemplate = usePostConvertTreatmentTemplate();

  const form = useForm<ConvertTreatmentTemplatePayload>({
    mode: "onSubmit",
    defaultValues: {
      duration_weeks: 20,
      group: undefined,
      template_id: templateID,
    },
    resolver: zodResolver(ConvertTreatmentTemplatePayloadSchema),
  });

  const onSubmit = form.handleSubmit(formData => {
    try {
      postConvertTreatmentTemplate.mutateAsync(formData, {
        onSuccess: () => {
          navigate("/library/your-library/templates");
        },
        onError: () => {
          setError(t("errors.generic"));
        },
      });
    } catch (e) {
      setError(t("errors.generic"));
    }
  });

  return (
    <MUIUnifiedModal open onClose={onClose} title={t("treatment.convert_template.title")}>
      <FormProvider {...form}>
        <Form onSubmit={onSubmit} noValidate>
          <Section>
            <Typography variant="subtitle2">{t("treatment.convert_template.body")}</Typography>
            <SectionTitle>{t("treatment.editor.settings_sidebar.program_length")}</SectionTitle>
            <Controller
              control={form.control}
              name="duration_weeks"
              render={({ field: { ref, ...field } }) => (
                <MUISelect {...field} options={DURATION_WEEKS_OPTIONS} value={field.value || ""} inputRef={ref} />
              )}
            />
          </Section>

          <Divider />

          <Section>
            <SectionTitle>{t("treatment.editor.settings_sidebar.group")}</SectionTitle>
            <Controller
              control={form.control}
              name="group"
              render={({ field: { ref, ...field } }) => (
                <MUISelect
                  {...field}
                  options={groupOptions}
                  value={field.value || ""}
                  inputRef={ref}
                  error={!!form.formState.errors.group}
                />
              )}
            />
          </Section>

          {(error || postConvertTreatmentTemplate.isError) && (
            <Notification variant="danger" style={{ marginTop: "20px" }}>
              {t("errors.generic")}
            </Notification>
          )}

          <DialogActions sx={{ marginTop: "16px" }}>
            <BaseButton text={t("buttons.cancel")} variant="secondary" onClick={onClose} style={{ flexGrow: 0 }} />
            <BaseButton
              text={t("buttons.convert")}
              type="submit"
              variant="primary"
              disabled={postConvertTreatmentTemplate.isLoading}
              style={{ flexGrow: 0 }}
            />
          </DialogActions>
        </Form>
      </FormProvider>
    </MUIUnifiedModal>
  );
};

const Form = styled.form`
  padding: 8px 16px 16px;
`;
